import { useT } from "@/lib/i18n"
import { useNavigate } from "@tanstack/react-router"
import { ArrowRight, ChevronsUpDown } from "lucide-react"
import { useId, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { type Scope, isValidScopeId, loadRecentScopes } from "@/lib/connection"
import { cn } from "@/lib/utils"

/** Production-like environments get a warm dot so nobody edits them by accident. */
export function envTone(environmentId: string): "prod" | "staging" | "dev" {
  const id = environmentId.toLowerCase()
  if (/(^|[-_.])(prod|production|live)($|[-_.])/.test(id)) return "prod"
  if (/(^|[-_.])(stag|staging|uat|preprod)($|[-_.])/.test(id)) return "staging"
  return "dev"
}

export function EnvDot({ environmentId, className }: { environmentId: string; className?: string }) {
  const tone = envTone(environmentId)
  return <span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", tone === "prod" ? "bg-destructive" : tone === "staging" ? "bg-warning" : "bg-success", className)} />
}

export function ScopeForm({ initial, onSubmit, submitLabel }: { initial?: Scope; onSubmit: (scope: Scope) => void; submitLabel?: string }) {
  const t = useT()
  const id = useId()
  const [orgId, setOrgId] = useState(initial?.orgId ?? "")
  const [environmentId, setEnvironmentId] = useState(initial?.environmentId ?? "")
  const orgValid = isValidScopeId(orgId.trim())
  const envValid = isValidScopeId(environmentId.trim())
  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (orgValid && envValid) onSubmit({ orgId: orgId.trim(), environmentId: environmentId.trim() })
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-org`}>{t("scope.organization")}</Label>
          <Input id={`${id}-org`} value={orgId} onChange={(e) => setOrgId(e.target.value)} placeholder="org_demo" autoComplete="off" spellCheck={false} className="font-mono text-xs" />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-env`}>{t("scope.environment")}</Label>
          <Input id={`${id}-env`} value={environmentId} onChange={(e) => setEnvironmentId(e.target.value)} placeholder="dev" autoComplete="off" spellCheck={false} className="font-mono text-xs" />
        </div>
      </div>
      <Button type="submit" variant="primary" disabled={!orgValid || !envValid}>
        {submitLabel ?? t("scope.open")} <ArrowRight className="rtl-mirror" />
      </Button>
    </form>
  )
}

export function ScopeSwitcher({ scope }: { scope: Scope }) {
  const t = useT()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const recent = loadRecentScopes().filter((s) => s.orgId !== scope.orgId || s.environmentId !== scope.environmentId)
  const go = (next: Scope) => {
    setOpen(false)
    void navigate({ to: "/$orgId/$environmentId", params: next })
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group flex w-full items-center gap-2 rounded-md border border-border-subtle bg-background/40 px-2.5 py-1.5 text-start transition-colors duration-150 hover:border-border hover:bg-raised"
          aria-label={t("scope.changeLabel", { orgId: scope.orgId, environmentId: scope.environmentId })}
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-foreground">{scope.orgId}</span>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <EnvDot environmentId={scope.environmentId} />
              <span className="truncate">{scope.environmentId}</span>
            </span>
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-subtle-foreground group-hover:text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0">
        {recent.length > 0 ? (
          <div className="border-b border-border-subtle p-1">
            <p className="px-2 pt-1.5 pb-1 text-2xs font-medium text-subtle-foreground">{t("scope.recent")}</p>
            {recent.map((s) => (
              <button
                key={`${s.orgId}/${s.environmentId}`}
                type="button"
                onClick={() => go(s)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm hover:bg-raised"
              >
                <EnvDot environmentId={s.environmentId} />
                <span className="truncate">{s.orgId}</span>
                <span className="ms-auto truncate text-xs text-muted-foreground">{s.environmentId}</span>
              </button>
            ))}
          </div>
        ) : null}
        <div className="p-3">
          <ScopeForm key={`${scope.orgId}/${scope.environmentId}`} initial={scope} onSubmit={go} submitLabel={t("scope.switch")} />
        </div>
      </PopoverContent>
    </Popover>
  )
}
